import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}

export default function ConfirmModal({
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger = false,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleEsc);
    confirmRef.current?.focus();
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onCancel]);

  return (
    <div className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div role="alertdialog" aria-labelledby="confirm-modal-title" aria-describedby="confirm-modal-desc"
           aria-modal="true" className="modal-content max-w-sm" dir="rtl">
        <div className="modal-header pb-0">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2.5 rounded-xl flex-shrink-0 ${danger ? 'bg-danger-50' : 'bg-warning-50'}`}>
              <AlertTriangle size={20} className={danger ? 'text-danger-600' : 'text-warning-600'} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="confirm-modal-title" className="text-lg font-semibold text-surface-900">{title}</h3>
              <p id="confirm-modal-desc" className="text-sm text-surface-500 mt-1">{description}</p>
            </div>
          </div>
          <button onClick={onCancel} aria-label={t('aria.close')}
            className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 hover:bg-surface-100 transition-colors flex-shrink-0">
            <X size={18} />
          </button>
        </div>
        <div className="modal-footer mt-4">
          <button onClick={onCancel} className="btn btn-secondary flex-1">
            {resolvedCancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className={`btn flex-1 ${danger ? 'btn-danger' : 'btn-primary'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
